import { forwardRef, useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { FormApi } from 'final-form';

import { Drive_Response_Reaction } from '@occc/graphql';
import { useAppLayoutContextConsumer } from '@occc-app/design-system';

import { ChatMessage } from './chat-message';
import { ChatForm } from './chat-form.component';

import type { ChatMessage as ChatMessageType } from '../types';
import { truncateText } from '../../utils/text-utils';
import { routeCreator } from '../../../drive-insights.router';

const rootSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

const scrollAreaSx: SxProps<Theme> = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
  px: { xs: 2, md: '130px' },
  maskImage: 'linear-gradient(to bottom, transparent 0%, black 120px, black 100%)',
  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 120px, black 100%)',
  scrollbarWidth: 'thin',
  scrollbarColor: '#d1d5db #f3f4ff',
};

const messagesContainerSx: SxProps<Theme> = {
  maxWidth: 720,
  mx: 'auto',
  width: '100%',
  pt: '96px',
  pb: 4,
  gap: '48px',
};

const inputContainerSx: SxProps<Theme> = {
  flexShrink: 0,
  px: { xs: 2, md: '130px' },
  pb: 2,
};

const inputInnerSx: SxProps<Theme> = {
  maxWidth: 720,
  mx: 'auto',
};

const disclaimerSx: SxProps<Theme> = {
  mt: 1.5,
  fontSize: 12,
  color: 'drive.textSecondary',
};

type ChatMessagesListProps = {
  messages: ChatMessageType[];
  onSubmit: (values: { question: string }) => void;
  formRef: React.MutableRefObject<FormApi<{ question: string }> | null>;
  onReactionChange: (responseId: string, reaction: Drive_Response_Reaction | null) => void;
  isStreaming?: boolean;
  processingMessage?: string | null;
  onRetry?: () => void;
};

const BREADCRUMB_TRUNCATE_LENGTH = 35;

export const ChatMessagesList = forwardRef<HTMLDivElement, ChatMessagesListProps>(
  (
    { messages, onSubmit, formRef, onReactionChange, isStreaming, processingMessage, onRetry },
    ref,
  ) => {
    const conversationName = useMemo(() => {
      const firstPrompt = messages.find((m) => m.type === 'prompt');
      return firstPrompt
        ? truncateText(firstPrompt.content, BREADCRUMB_TRUNCATE_LENGTH)
        : 'Untitled';
    }, [messages]);

    const breadcrumbLinks = useMemo(
      () => [{ label: 'Insights', to: routeCreator.base() }, { label: conversationName }],
      [conversationName],
    );

    useAppLayoutContextConsumer(breadcrumbLinks);

    return (
      <Box sx={rootSx}>
        <Box ref={ref} sx={scrollAreaSx} role='log' aria-label='Conversation messages'>
          <Stack sx={messagesContainerSx}>
            {messages.map((message) => (
              <ChatMessage
                key={message.clientId || message.id}
                message={message}
                onReactionChange={onReactionChange}
                onRetry={onRetry}
                streamingStatusText={
                  isStreaming &&
                  message.type === 'response' &&
                  message.isStreaming &&
                  processingMessage
                    ? processingMessage
                    : null
                }
              />
            ))}
          </Stack>
        </Box>

        <Box sx={inputContainerSx}>
          <Box sx={inputInnerSx}>
            <ChatForm onSubmit={onSubmit} formRef={formRef} isStreaming={isStreaming} />
            <Typography variant='caption' component='p' align='center' sx={disclaimerSx}>
              Insights can make mistakes. Check important info.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  },
);

ChatMessagesList.displayName = 'ChatMessagesList';
